import { useState } from 'react'
import { useApi, useApiMutation } from '../hooks/useApi'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { Input } from '../components/ui/Input'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { Badge } from '../components/ui/Badge'
import { formatRelativeTime, getStatusIcon } from '../lib/utils'
import type { Deployment, Customer, CreateDeploymentRequest } from '../types'

export function Deployments() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState('')
  const [version, setVersion] = useState('v1.0.0')

  // Fetch deployments and customers
  const { data: deployments, loading: deploymentsLoading, error: deploymentsError, refetch } = useApi<Deployment[]>('/deployments')
  const { data: customers, loading: customersLoading } = useApi<Customer[]>('/customers')
  
  // Create deployment mutation
  const { mutate: createDeployment, loading: creatingDeployment } = useApiMutation<Deployment, CreateDeploymentRequest>()

  const handleCreateDeployment = async () => {
    if (!selectedCustomer || !version) return

    try {
      await createDeployment('/deployments', {
        customer_id: selectedCustomer,
        version: version,
      })
      
      // Reset form and close modal
      setSelectedCustomer('')
      setVersion('v1.0.0')
      setIsCreateModalOpen(false)
      
      // Refresh deployments list
      refetch()
    } catch (error) {
      console.error('Failed to create deployment:', error)
    }
  }

  if (deploymentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Deployments</h1>
        <p className="mt-2 text-sm text-gray-700">
          Track and manage deployment operations
        </p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Deployment History
            </h3>
            <Button 
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              New Deployment
            </Button>
          </div>
          
          <div className="mt-5">
            {deploymentsError ? (
              <div className="text-sm text-red-600">
                Error loading deployments: {deploymentsError}
              </div>
            ) : deployments && deployments.length > 0 ? (
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Version
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Started
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deployments.map((deployment) => (
                      <tr key={deployment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {deployment.customer_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {deployment.version}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge 
                            variant={
                              deployment.status === 'completed' ? 'success' :
                              deployment.status === 'failed' ? 'destructive' :
                              deployment.status === 'in_progress' ? 'info' : 'default'
                            }
                          >
                            {getStatusIcon(deployment.status)} {deployment.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatRelativeTime(deployment.started_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {deployment.completed_at ? 
                            `${Math.round((new Date(deployment.completed_at).getTime() - new Date(deployment.started_at).getTime()) / 1000)}s` :
                            'In progress'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                No deployments found. Create a customer first to start deploying.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Deployment Modal */}
      <Modal 
        isOpen={isCreateModalOpen} 
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Deployment"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer *
            </label>
            {customersLoading ? (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                required
              >
                <option value="">Select a customer</option>
                {customers?.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} ({customer.id})
                  </option>
                ))}
              </select>
            )}
          </div>

          <Input
            label="Version *"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="v1.0.0"
            helperText="Semantic version for this deployment"
            required
          />

          <div className="flex items-center justify-end space-x-3 pt-4">
            <Button 
              variant="ghost" 
              onClick={() => setIsCreateModalOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateDeployment}
              loading={creatingDeployment}
              disabled={!selectedCustomer || !version}
            >
              Create Deployment
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}